<template>
	<b-form novalidate class="FulfilmentOverrides-Form" @submit.prevent="onSave">
		<OfsPanel>
			<ContentHeader
				:title="fulfilmentOverrideId ? $t('Edit Fulfilment Override') : $t('Create Fulfilment Override')"
				class="mb-3"
				no-padding
			/>
			<div class="row">
				<div class="col-6">
					<OfFormSelect name="productId" :label="$t('Product')" :options="productOptions" required />
				</div>
			</div>

			<br />

			<ContentHeader :title="$t('Rule')" class="mb-3" no-padding />

			<div class="row">
				<div class="col-6">
					<p>
						<b>Default behavior:</b> when processing an item, the available <b>Stock Location</b> is checked based on
						the order region. If an item is available in multiple locations, the first location is used.
					</p>
					<p>
						This behavior can be modified for a specific product by specifying an additional rule.
					</p>
					<p>
						<b>Skip Stock</b> - the inventory check is skipped, and the order is shipped according to the published
						<b>Product Specification</b>.
					</p>
					<p>
						<b>Force Stock Location</b> - it can be used when multiple <b>Stock Locations</b> are configured for a
						single region (check default behavior). In that case, <b>the Stock Location</b> specified in the rule is
						used.
					</p>
				</div>
			</div>

			<div class="row">
				<div class="col-12">
					<FulfilmentOverrideRule
						v-if="formData.condition"
						:rule="formData.condition"
						:form-path="`condition`"
						class="mb-2"
					/>
				</div>
			</div>

			<div v-if="isForceStockSelected" class="row">
				<div class="col-6">
					<OfFormSelect name="locationId" :label="$t('Stock Location')" :options="stockLocationOptions" required />
				</div>
			</div>

			<template slot="actions">
				<of-submit-button v-t="'Save'" data-test-id="attribute-save" variant="primary" />
			</template>
		</OfsPanel>
	</b-form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
	OfsPanel,
	ContentHeader,
	withForm,
	OfFormInput,
	OfSubmitButton,
	OfFormSelect,
	validateWithMessage,
	OfMultiSelect
} from '@oneflow/ofs-vue-layout';
import Draggable from 'vuedraggable';
import { $t } from '@/vuex';
import { codeSafe } from '@/lib/validators';
import { minValue, maxValue, minLength, maxLength } from 'vuelidate/lib/validators';
import FulfilmentOverrideRule from '@/components/JsonLogic/FulfilmentOverrideRule';

const initialFormData = {
	productId: '',
	condition: {
		if: [
			{
				and: [
					{
						'==': [
							{
								var: 'order.channel.source'
							},
							'Found.us'
						]
					}
				]
			},
			'skipStock'
		]
	}
};

export default {
	components: {
		OfsPanel,
		ContentHeader,
		OfSubmitButton,
		OfFormSelect,
		FulfilmentOverrideRule
	},
	mixins: [withForm('FulfilmentOverridesForm')],
	data() {
		return {
			isDragging: false
		};
	},
	computed: {
		...mapGetters({
			stockLocations: 'stock-location/stock-locations',
			products: 'product/products'
		}),
		fulfilmentOverrideId() {
			const { id } = this.$route.params;
			return id !== 'new' ? id : null;
		},
		productOptions() {
			const defaultOption = { text: this.$t('None'), value: null };
			return [defaultOption, ...this.products.data.map(product => ({ text: product.name, value: product._id }))];
		},
		stockLocationOptions() {
			const defaultOption = { text: this.$t('None'), value: null };
			return [
				defaultOption,
				...this.stockLocations.data.map(location => ({ text: location.name, value: location._id }))
			];
		},
		isForceStockSelected() {
			const action = _.get(this.formData.condition, 'if.[1]');
			return action === 'forceStockLocation';
		}
	},
	async mounted() {
		try {
			await this.fetchStockLocations({ query: { query: { $limit: 1000 } } });
			await this.getProducts({
				query: { query: { $sort: { name: 1 }, $limit: 1000, $select: ['_id', 'id', 'name'] } }
			});

			if (!this.fulfilmentOverrideId) {
				return this.initFormData(initialFormData);
			}

			const fulfilmentOverride = await this.findFulfilmentOverrideById({
				id: this.fulfilmentOverrideId
			});

			this.initFormData(fulfilmentOverride);
		} catch (err) {
			this.$notify({
				type: 'error',
				title: this.$t('Error'),
				text: this.$t('An error occurred while fetching fulfilment override')
			});
		}
	},
	methods: {
		...mapActions({
			findFulfilmentOverrideById: 'fulfilment-override/findById',
			createFulfilmentOverride: 'fulfilment-override/create',
			updateFulfilmentOverride: 'fulfilment-override/update',
			fetchStockLocations: 'stock-location/find',
			getProducts: 'product/find'
		}),
		async onSave() {
			try {
				let message = $t('Fulfilment override updated successfully');
				const payload = !this.isForceStockSelected ? _.omit(this.formData, ['locationId']) : this.formData;

				if (this.formData._id) {
					await this.dispatchSubmit(this.updateFulfilmentOverride({ id: this.formData._id, data: payload }));
				} else {
					message = $t('Fulfilment override created successfully');
					const response = await this.dispatchSubmit(this.createFulfilmentOverride(payload));

					this.updateField('_id', response._id);
					this.$router.push({
						name: 'fulfilment-overrides.edit',
						params: { id: response._id }
					});
				}
				this.$notify({ type: 'success', text: message });
			} catch (err) {
				this.$notify({ type: 'error', text: err.message });
			}
		},
		addProductionRule(path) {
			const currentProductionRules = _.get(this.formData, path, []);
			const payoutRule = { if: [{ and: [true] }, '', ''] };

			this.updateField(path, [...currentProductionRules, payoutRule]);
		},
		removeProductionRule(index, path) {
			const currentProductionRules = _.get(this.formData, path, []);
			const filteredProductionRules = _.filter(currentProductionRules, (_, idx) => idx !== index);

			this.updateField(path, filteredProductionRules);
		},
		handleDragStart() {
			this.isDragging = true;
		},
		handleDragEnd() {
			setTimeout(() => {
				this.isDragging = false;
			}, 300);
		},
		handleOrderChange() {
			// trigger the change detection
			this.updateFormData({
				productionRules: [...this.formData.productionRules]
			});
		}
	}
};
</script>
